import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '@/context/AuthContext';
import '@/styles/PropertyPagesModern.scss';
import * as Sentry from '@sentry/react';

/**
 * PropertyExternalMappingsPage Component
 * 
 * Administrative page for managing external system property mappings.
 * Allows admins to link CapTrac properties to external systems (MDA, Marketing, SIPA).
 * Features:
 * - View all properties and their external mappings
 * - Add/Edit/Delete mappings for each external system using searchable dropdowns
 * - Search and filter properties
 * - Bulk operations support
 */
const PropertyExternalMappingsPage = () => {
    const { apiFetch } = useAuth();
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [editingMapping, setEditingMapping] = useState(null);
    const [showMappingModal, setShowMappingModal] = useState(false);
    const [externalProperties, setExternalProperties] = useState({
        MDA: [],
        Marketing: [],
        SIPA: [],
        RetailDynamics: [],
        Assets: []
    });
    const [loadingExternalProperties, setLoadingExternalProperties] = useState(false);
    const [externalPropertySearch, setExternalPropertySearch] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);

    // External systems configuration
    const externalSystems = ['MDA', 'Marketing', 'SIPA', 'RetailDynamics', 'Assets'];

    /**
     * Fetches all properties with their external mappings on component mount.
     */
    useEffect(() => {
        fetchProperties();
        fetchAllExternalProperties();
    }, []);

    /**
     * Fetches properties from all external systems.
     */
    const fetchAllExternalProperties = async () => {
        setLoadingExternalProperties(true);
        try {
            const [mdaResponse, marketingResponse, sipaResponse, retailDynamicsResponse, assetsResponse] = await Promise.all([
                apiFetch('/api/Property/external-systems/mda/properties'),
                apiFetch('/api/Property/external-systems/marketing/properties'),
                apiFetch('/api/Property/external-systems/sipa/properties'),
                apiFetch('/api/Property/external-systems/retaildynamics/properties'),
                apiFetch('/api/Property/external-systems/assets/properties')
            ]);

            const mdaData = await mdaResponse.json();
            const marketingData = await marketingResponse.json();
            const sipaData = await sipaResponse.json();
            const retailDynamicsData = await retailDynamicsResponse.json();
            const assetsData = await assetsResponse.json();

            setExternalProperties({
                MDA: mdaData.success ? mdaData.data : [],
                Marketing: marketingData.success ? marketingData.data : [],
                SIPA: sipaData.success ? sipaData.data : [],
                RetailDynamics: retailDynamicsData.success ? retailDynamicsData.data : [],
                Assets: assetsData.success ? assetsData.data : []
            });
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching external properties:', error);
            toast.error('Error loading external system properties');
        } finally {
            setLoadingExternalProperties(false);
        }
    };

    /**
     * Fetches all properties from the API.
     */
    const fetchProperties = async () => {
        try {
            setLoading(true);
            const response = await apiFetch('/api/Property/all');
            const data = await response.json();

            if (data.success && data.data) {
                // Fetch external mappings for each property
                const propertiesWithMappings = await Promise.all(
                    data.data.map(async (property) => {
                        const mappingsResponse = await apiFetch(
                            `/api/Property/${property.propertyID}/external-mappings`
                        );
                        const mappingsData = await mappingsResponse.json();
                        
                        return {
                            ...property,
                            externalMappings: mappingsData.success ? mappingsData.data : []
                        };
                    })
                );

                setProperties(propertiesWithMappings);
            } else {
                toast.error('Failed to load properties');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching properties:', error);
            toast.error('Error loading properties');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Opens the mapping modal for editing or creating a mapping.
     * @param {Object} property - The property to map
     * @param {Object|null} mapping - Existing mapping to edit, or null for new mapping
     */
    const openMappingModal = (property, mapping = null) => {
        setSelectedProperty(property);
        setEditingMapping(mapping || {
            propertyID: property.propertyID,
            externalSystem: 'MDA',
            externalPropertyID: '',
            externalPropertyName: '',
            isActive: true,
            notes: ''
        });
        setExternalPropertySearch('');
        setShowDropdown(false);
        setShowMappingModal(true);
    };

    /**
     * Handles selection of an external property from dropdown.
     * @param {Object} selectedOption - The selected external property
     */
    const handleExternalPropertySelect = (selectedOption) => {
        if (selectedOption) {
            setEditingMapping({
                ...editingMapping,
                externalPropertyID: selectedOption.id,
                externalPropertyName: selectedOption.name
            });
            setExternalPropertySearch(selectedOption.displayText);
            setShowDropdown(false);
        }
    };

    /**
     * Closes the mapping modal and resets state.
     */
    const closeMappingModal = () => {
        setShowMappingModal(false);
        setSelectedProperty(null);
        setEditingMapping(null);
        setExternalPropertySearch('');
        setShowDropdown(false);
    };

    /**
     * Saves the external mapping (create or update).
     */
    const saveMapping = async () => {
        try {
            const response = await apiFetch('/api/Property/external-mappings', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(editingMapping)
            });

            const data = await response.json();

            if (data.success) {
                toast.success('Mapping saved successfully');
                closeMappingModal();
                fetchProperties(); // Refresh the list
            } else {
                toast.error(data.message || 'Failed to save mapping');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error saving mapping:', error);
            toast.error('Error saving mapping');
        }
    };

    /**
     * Deletes an external mapping.
     * @param {number} mappingID - ID of the mapping to delete
     */
    const deleteMapping = async (mappingID) => {
        if (!confirm('Are you sure you want to delete this mapping?')) {
            return;
        }

        try {
            const response = await apiFetch(`/api/Property/external-mappings/${mappingID}`, {
                method: 'DELETE'
            });

            const data = await response.json();

            if (data.success) {
                toast.success('Mapping deleted successfully');
                fetchProperties(); // Refresh the list
            } else {
                toast.error(data.message || 'Failed to delete mapping');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error deleting mapping:', error);
            toast.error('Error deleting mapping');
        }
    };

    /**
     * Gets the mapping for a specific property and external system.
     * @param {Object} property - The property
     * @param {string} system - The external system name
     * @returns {Object|null} The mapping or null if not found
     */
    const getMappingForSystem = (property, system) => {
        return property.externalMappings?.find(
            m => m.externalSystem === system && m.isActive
        ) || null;
    };

    /**
     * Filters properties based on search term.
     */
    const filteredProperties = properties.filter(property =>
        property.propertyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        property.comparativeName?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    /**
     * Gets filtered external properties for dropdown based on search.
     */
    const getFilteredExternalProperties = () => {
        if (!externalPropertySearch || !editingMapping) return [];
        
        const systemProperties = externalProperties[editingMapping.externalSystem] || [];
        return systemProperties
            .filter(prop => 
                prop.displayText.toLowerCase().includes(externalPropertySearch.toLowerCase())
            )
            .slice(0, 15); // Limit to 15 results for performance
    };

    if (loading) {
        return (
            <div className="property-external-mappings-page">
                <div className="loading-spinner">Loading properties...</div>
            </div>
        );
    }

    return (
        <div className="property-external-mappings-page">
<div className="page-header">
                <h1>Property External System Mappings</h1>
                <p className="subtitle">
                    Link CapTrac properties to external systems (MDA, Marketing, SIPA) for integrated reporting
                </p>
            </div>

            <div className="search-section">
                <input
                    type="text"
                    placeholder="Search properties..."
                    className="search-input"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            <div className="mappings-table-container">
                <table className="mappings-table">
                    <thead>
                        <tr>
                            <th>Property Name</th>
                            {externalSystems.map(system => (
                                <th key={system}>{system}</th>
                            ))}
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredProperties.map(property => (
                            <tr key={property.propertyID}>
                                <td className="property-name">
                                    <div className="property-name-cell">
                                        <strong>{property.propertyName}</strong>
                                        {property.comparativeName && (
                                            <small>({property.comparativeName})</small>
                                        )}
                                    </div>
                                </td>
                                {externalSystems.map(system => {
                                    const mapping = getMappingForSystem(property, system);
                                    return (
                                        <td key={system} className="mapping-cell">
                                            {mapping ? (
                                                <div className="mapping-info">
                                                    <div className="mapping-id">
                                                        ID: {mapping.externalPropertyID}
                                                    </div>
                                                    {mapping.externalPropertyName && (
                                                        <div className="mapping-name">
                                                            {mapping.externalPropertyName}
                                                        </div>
                                                    )}
                                                    <div className="mapping-actions">
                                                        <button
                                                            className="btn-edit-small"
                                                            onClick={() => openMappingModal(property, mapping)}
                                                            title="Edit mapping"
                                                        >
                                                            ✏️
                                                        </button>
                                                        <button
                                                            className="btn-delete-small"
                                                            onClick={() => deleteMapping(mapping.id)}
                                                            title="Delete mapping"
                                                        >
                                                            🗑️
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <button
                                                    className="btn-add-mapping"
                                                    onClick={() => {
                                                        const newMapping = {
                                                            propertyID: property.propertyID,
                                                            externalSystem: system,
                                                            externalPropertyID: '',
                                                            externalPropertyName: '',
                                                            isActive: true,
                                                            notes: ''
                                                        };
                                                        openMappingModal(property, newMapping);
                                                    }}
                                                >
                                                    + Add
                                                </button>
                                            )}
                                        </td>
                                    );
                                })}
                                <td>
                                    <button
                                        className="btn-view-all"
                                        onClick={() => {
                                            setSelectedProperty(property);
                                            // Could open a detailed view modal here
                                        }}
                                    >
                                        View All
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Mapping Edit Modal */}
            {showMappingModal && editingMapping && (
                <div className="modal-overlay" onClick={closeMappingModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>
                                {editingMapping.id ? 'Edit' : 'Add'} External Mapping
                            </h2>
                            <button className="close-button" onClick={closeMappingModal}>
                                ×
                            </button>
                        </div>

                        <div className="modal-body">
                            <div className="form-group">
                                <label>Property</label>
                                <input
                                    type="text"
                                    value={selectedProperty?.propertyName || ''}
                                    disabled
                                    className="form-control"
                                />
                            </div>

                            <div className="form-group">
                                <label>External System *</label>
                                <select
                                    className="form-control"
                                    value={editingMapping.externalSystem}
                                    onChange={(e) => {
                                        setEditingMapping({
                                            ...editingMapping,
                                            externalSystem: e.target.value,
                                            externalPropertyID: '',
                                            externalPropertyName: ''
                                        });
                                        setExternalPropertySearch('');
                                        setShowDropdown(false);
                                    }}
                                >
                                    {externalSystems.map(system => (
                                        <option key={system} value={system}>
                                            {system}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Search and Select External Property *</label>
                                {loadingExternalProperties ? (
                                    <div className="loading-text">Loading properties from {editingMapping.externalSystem}...</div>
                                ) : (
                                    <div className="autocomplete-wrapper">
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={externalPropertySearch}
                                            onChange={(e) => {
                                                setExternalPropertySearch(e.target.value);
                                                setShowDropdown(true);
                                            }}
                                            onFocus={() => externalPropertySearch && setShowDropdown(true)}
                                            placeholder={`Type to search ${editingMapping.externalSystem} properties...`}
                                        />
                                        {showDropdown && externalPropertySearch && (
                                            <div className="dropdown-list">
                                                {getFilteredExternalProperties().length > 0 ? (
                                                    getFilteredExternalProperties().map(prop => (
                                                        <div
                                                            key={prop.id}
                                                            className="dropdown-item"
                                                            onClick={() => handleExternalPropertySelect(prop)}
                                                        >
                                                            {prop.displayText}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="dropdown-item-empty">No properties found</div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="form-group">
                                <label>Selected External Property</label>
                                <div className="selected-property-display">
                                    {editingMapping.externalPropertyID ? (
                                        <>
                                            <strong>ID:</strong> {editingMapping.externalPropertyID}
                                            {editingMapping.externalPropertyName && (
                                                <>
                                                    <br />
                                                    <strong>Name:</strong> {editingMapping.externalPropertyName}
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        <span className="text-muted">No property selected. Start typing to search.</span>
                                    )}
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Notes</label>
                                <textarea
                                    className="form-control"
                                    value={editingMapping.notes || ''}
                                    onChange={(e) =>
                                        setEditingMapping({
                                            ...editingMapping,
                                            notes: e.target.value
                                        })
                                    }
                                    placeholder="Additional notes about this mapping"
                                    rows="3"
                                />
                            </div>

                            <div className="form-group checkbox-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={editingMapping.isActive}
                                        onChange={(e) =>
                                            setEditingMapping({
                                                ...editingMapping,
                                                isActive: e.target.checked
                                            })
                                        }
                                    />
                                    Active
                                </label>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button
                                className="btn-secondary"
                                onClick={closeMappingModal}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-primary"
                                onClick={saveMapping}
                                disabled={!editingMapping.externalPropertyID}
                            >
                                Save Mapping
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PropertyExternalMappingsPage;
